import React, { useRef, useState } from 'react';
import { graphql, PageProps } from 'gatsby';
import { HealthMap, LogosGroup, scrollToMap } from '@components';
import { TUnIndexPageProperties } from '.';
import {
    StyledSection,
    StyledSectionBox,
    StyledLogosBox,
    StyledMapWrapper,
    StyledSectionTitle,
    StyledSectionSubtitle,
    StyledAreasGrid,
    StyledAreasCard,
    StyledAreasCardActionArea,
    StyledAreasCardContent,
    StyledAreasCardTopGrid,
    StyledAreasCardBottomGrid,
    StyledExploreGrid,
    StyledCardGrid,
    StyledCard,
    StyledCardActionArea,
    StyledCardMedia,
    StyledCardContent,
    StyledCardHeader,
    StyledExploreCardBox,
    StyledCardDescription,
    StyledExploreCardCta,
    StyledArrowForwardIcon,
    StyledResourcesGrid,
    StyledResourcesCardCta,
    StyledContentSectionTitle,
    StyledAcknowledgementsTitle,
    StyledAcknowledgementsTypography,
} from './index-unscr2286.styled';
import {
    health,
    logos,
    areasOfConcern,
    TMapSettings,
    otherMaps,
    furtherResources,
} from '@constants';
import { Grid, Typography } from '@material-ui/core';
import { THealthSecurityIncident } from '@types';
import { calculateFilteredTotals } from '@helpers';
import UNSCR2286HeroSection from 'components/UNSCR2286HeroSection/unscr2286-hero-section';

import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

export default function indexUNSCR2286({
    data,
}: PageProps<TUnIndexPageProperties>) {
    const { markdownRemark } = data;

    const [selectedArea, setSelectedArea] = useState<
        TMapSettings | undefined
    >();
    const [isLoading, setIsLoading] = useState(true);
    const globalStatistics = useRef();

    const handleAreaClick = (mapSettings: TMapSettings) => {
        setSelectedArea(mapSettings);
        scrollToMap();
        // Set to undefined to trigger useEffect() when clicking twice on same card
        setTimeout(() => setSelectedArea(), 500);
    };

    const setGlobalStatistics = (allIncidents: THealthSecurityIncident[]) => {
        const globalTotals = calculateFilteredTotals(allIncidents);
        globalStatistics.current = globalTotals;
        setIsLoading(false);
    };

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <main>
            <UNSCR2286HeroSection
                isLoading={isLoading}
                globalStatistics={globalStatistics}
            />

            <StyledMapWrapper id="mapSection">
                <HealthMap
                    frontmatter={markdownRemark.frontmatter}
                    hideAbout={true}
                    hideAllCategoriesOption={true}
                    hideHeader={true}
                    selectedCategory={'Conflict'}
                    selectedArea={selectedArea}
                    scrollZoomRequiresFocus={true}
                    setGlobalStatistics={setGlobalStatistics}
                />
            </StyledMapWrapper>

            <StyledSection>
                <StyledSectionBox>
                    <StyledSectionTitle variant="h4">
                        AREAS OF CONCERN
                    </StyledSectionTitle>
                    <StyledSectionSubtitle>
                        Apply pre-selected filters to analyse specific contexts
                    </StyledSectionSubtitle>

                    <StyledAreasGrid>
                        {areasOfConcern.map(
                            ({ id, title, icon, subHeading, mapSettings }) => {
                                const CountryOutlineIcon = icon;
                                const CategoryIcon = health.filters.categoryOptions.find(
                                    categoryOption =>
                                        categoryOption.value ===
                                        mapSettings.category
                                ).icon;

                                return (
                                    <StyledAreasCard
                                        variant="outlined"
                                        key={id}
                                    >
                                        <StyledAreasCardActionArea
                                            onClick={() =>
                                                handleAreaClick(mapSettings)
                                            }
                                        >
                                            <StyledAreasCardContent>
                                                <StyledAreasCardTopGrid
                                                    container
                                                    alignItems="center"
                                                    justify="center"
                                                    spacing={2}
                                                >
                                                    <Grid item zeroMinWidth>
                                                        <CountryOutlineIcon
                                                            style={{
                                                                width: 40,
                                                                height: 40,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="h4">
                                                            {title}
                                                        </Typography>
                                                    </Grid>
                                                </StyledAreasCardTopGrid>
                                                <StyledAreasCardBottomGrid
                                                    container
                                                    alignItems="center"
                                                    justify="center"
                                                    spacing={2}
                                                >
                                                    <Grid item zeroMinWidth>
                                                        <CategoryIcon
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="h6">
                                                            {subHeading}
                                                        </Typography>
                                                    </Grid>
                                                </StyledAreasCardBottomGrid>
                                            </StyledAreasCardContent>
                                        </StyledAreasCardActionArea>
                                    </StyledAreasCard>
                                );
                            }
                        )}
                    </StyledAreasGrid>
                </StyledSectionBox>
            </StyledSection>
            <StyledSection>
                <StyledSectionBox>
                    <StyledSectionTitle variant="h4">
                        EXPLORE MORE
                    </StyledSectionTitle>
                    <StyledSectionSubtitle>
                        Interactive maps from our data collection
                    </StyledSectionSubtitle>

                    <StyledExploreGrid
                        container
                        spacing={isSmallScreen ? 4 : 10}
                    >
                        {otherMaps.map(
                            ({
                                id,
                                header,
                                image,
                                imageTitle,
                                description,
                                url,
                            }) => {
                                return (
                                    <StyledCardGrid
                                        item
                                        sm={12}
                                        md={6}
                                        key={id}
                                    >
                                        <StyledCard
                                            elevation={0}
                                            variant="outlined"
                                        >
                                            <StyledCardActionArea
                                                component="a"
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <StyledCardMedia
                                                    image={image}
                                                    title={imageTitle}
                                                />
                                                <StyledCardContent>
                                                    <StyledCardHeader
                                                        variant="h5"
                                                        align="left"
                                                    >
                                                        {header}
                                                    </StyledCardHeader>
                                                    <StyledExploreCardBox
                                                        display="flex"
                                                        style={{
                                                            flexWrap: isSmallScreen
                                                                ? 'wrap'
                                                                : 'nowrap',
                                                        }}
                                                        alignItems="end"
                                                    >
                                                        <StyledCardDescription
                                                            variant="caption"
                                                            align="left"
                                                        >
                                                            {description}
                                                        </StyledCardDescription>
                                                        <StyledExploreCardCta
                                                            variant="caption"
                                                            align="right"
                                                        >
                                                            View map
                                                            <StyledArrowForwardIcon fontSize="inherit" />
                                                        </StyledExploreCardCta>
                                                    </StyledExploreCardBox>
                                                </StyledCardContent>
                                            </StyledCardActionArea>
                                        </StyledCard>
                                    </StyledCardGrid>
                                );
                            }
                        )}
                    </StyledExploreGrid>
                </StyledSectionBox>
            </StyledSection>
            <StyledSection>
                <StyledSectionBox>
                    <StyledSectionTitle variant="h4">
                        FURTHER RESOURCES
                    </StyledSectionTitle>
                    <StyledSectionSubtitle>
                        Browse archived content across our resource libraries
                    </StyledSectionSubtitle>

                    <StyledResourcesGrid
                        container
                        spacing={isSmallScreen ? 4 : 10}
                    >
                        {furtherResources.map(
                            ({ id, icon, title, description, url }) => {
                                const Icon = icon;

                                return (
                                    <StyledCardGrid
                                        item
                                        sm={12}
                                        md={4}
                                        key={id}
                                    >
                                        <StyledCard
                                            elevation={0}
                                            variant="outlined"
                                        >
                                            <StyledCardActionArea
                                                component="a"
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <StyledCardContent>
                                                    <StyledCardHeader
                                                        variant="h5"
                                                        align="left"
                                                    >
                                                        <Icon
                                                            fontSize="inherit"
                                                            style={{
                                                                marginRight: 6,
                                                                verticalAlign:
                                                                    'middle',
                                                            }}
                                                        />
                                                        {title}
                                                    </StyledCardHeader>
                                                    <StyledCardDescription
                                                        variant="caption"
                                                        align="left"
                                                    >
                                                        {description}
                                                    </StyledCardDescription>
                                                    <StyledResourcesCardCta
                                                        variant="caption"
                                                        align="right"
                                                    >
                                                        View archive
                                                        <StyledArrowForwardIcon fontSize="inherit" />
                                                    </StyledResourcesCardCta>
                                                </StyledCardContent>
                                            </StyledCardActionArea>
                                        </StyledCard>
                                    </StyledCardGrid>
                                );
                            }
                        )}
                    </StyledResourcesGrid>
                </StyledSectionBox>
            </StyledSection>
            <StyledSection>
                <StyledSectionBox>
                    <StyledContentSectionTitle variant="h4">
                        METHODOLOGY
                    </StyledContentSectionTitle>
                    <Typography variant="h5" align="justify" gutterBottom>
                        Methodology on collection of data
                    </Typography>
                    <Typography align="justify" paragraph>
                        The data displayed on the global map has been collated
                        by Insecurity Insight from a range of different sources,
                        including incidents collected through our open-source
                        monitoring and incidents shared through our partner
                        agencies. It includes any reported act of verbal or
                        physical violence, obstruction, threat of violence that
                        interferes with the availability of, access to, and
                        delivery of curative and/or preventative health services
                        perpetrated by state and non-state actors, patients or
                        relatives of patients, private individuals, and
                        criminals. Data starts from 2016, coinciding with the UN
                        Security Council resolution 2286 to protect health care
                        in conflict, and is updated on a daily basis.
                    </Typography>
                    <Typography align="justify" paragraph>
                        It displays the total number of reported incidents
                        involving an attack on a health system, the total number
                        of incidents involving an attack on a health facility
                        resulting in damage or destruction to the facility, and
                        the total number of health workers killed, arrested or
                        kidnapped since 01 January 2016.
                    </Typography>
                    <Typography align="justify" paragraph>
                        For more information on Insecurity Insight’s methodology
                        and the definitions used for the data presented in this
                        map, see the relevant{' '}
                        <a
                            href="https://bit.ly/HealthMapDataExplainer"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Data Explainer
                        </a>
                        .
                    </Typography>
                    <Typography variant="h5" align="justify" gutterBottom>
                        Use and Citation
                    </Typography>
                    <Typography align="justify" paragraph>
                        Any use of Insecurity Insight’s data, including in
                        reports, documents, and visuals, should cite the source,
                        the data selection and the data period.
                    </Typography>
                    <Typography variant="h5" align="justify" gutterBottom>
                        Suggested citation format
                    </Typography>
                    <Typography align="justify" paragraph>
                        Insecurity Insight. 10 Years of Attacks on Health Care:
                        Interactive Map. Available at
                        https://maps.insecurityinsight.org. Data retrieved on
                        [DD MM YYYY].
                    </Typography>
                    <Typography align="justify" paragraph>
                        Note: Insecurity Insight is continuously updating and
                        backdating the Attacks on Health dataset and numbers
                        continuously change, therefore the date when the data
                        was accessed must be included.
                    </Typography>
                </StyledSectionBox>
            </StyledSection>
            <StyledSection>
                <StyledSectionBox>
                    <StyledAcknowledgementsTitle variant="h4">
                        ACKNOWLEDGEMENTS
                    </StyledAcknowledgementsTitle>
                    <Typography>
                        Map created by Insecurity Insight for the Safeguarding
                        Health in Conflict Coalition with initial support from
                        Map Action
                    </Typography>
                    <StyledLogosBox>
                        <LogosGroup
                            logos={[
                                ...logos.mainLogos,
                                ...logos.mapactionLogos,
                            ]}
                            logoWidth="100px"
                            invert
                        ></LogosGroup>
                    </StyledLogosBox>
                    <StyledAcknowledgementsTypography>
                        In collaboration with
                    </StyledAcknowledgementsTypography>
                    <StyledLogosBox>
                        <LogosGroup
                            logos={logos.collaborationLogos}
                            logoWidth="120px"
                            invert
                        ></LogosGroup>
                    </StyledLogosBox>
                    <StyledAcknowledgementsTypography>
                        Funded by
                    </StyledAcknowledgementsTypography>
                    <StyledLogosBox>
                        <LogosGroup
                            logos={logos.fundingLogos}
                            logoWidth="120px"
                            invert
                        ></LogosGroup>
                    </StyledLogosBox>
                </StyledSectionBox>
            </StyledSection>
        </main>
    );
}

export const pageQuery = graphql`
    query indexUNSCR2286PageTemplate {
        markdownRemark(frontmatter: { path: { eq: "" } }) {
            frontmatter {
                path
                metadataPageTitle
                healthMapHeading
                healthMapSubheading
                metadataPageDescription
                healthMapSideBarCopy
            }
        }
    }
`;
